import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { TimezoneGroup } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface TimezoneState {
  groups: TimezoneGroup[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: TimezoneState = {
  groups: [],
  isLoading: false,
  error: null,
  lastFetched: null,
};

// Cache duration: 1 hour
const CACHE_DURATION_MS = 60 * 60 * 1000;

export const fetchTimezones = createAsyncThunk(
  'timezone/fetchTimezones',
  async (_, { getState, rejectWithValue }) => {
    // Skip fetch if data was fetched within the cache window
    const state = getState() as { timezone: TimezoneState };
    if (
      state.timezone.lastFetched &&
      state.timezone.groups.length > 0 &&
      Date.now() - state.timezone.lastFetched < CACHE_DURATION_MS
    ) {
      return state.timezone.groups;
    }

    try {
      const response = await api.get<{ success: boolean; data: TimezoneGroup[] }>(endpoints.timezones.list);
      return response.data.data as TimezoneGroup[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch timezones'));
    }
  }
);

const timezoneSlice = createSlice({
  name: 'timezone',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchTimezones.pending, (state) => {
      // Only show loading if we don't have cached data
      if (state.groups.length === 0) {
        state.isLoading = true;
      }
      state.error = null;
    });
    builder.addCase(fetchTimezones.fulfilled, (state, action) => {
      state.isLoading = false;
      state.groups = action.payload;
      state.lastFetched = Date.now();
    });
    builder.addCase(fetchTimezones.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export default timezoneSlice.reducer;
